import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';

const TripBookingForm = ({ onTripCreated, reBookData }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState(null);
  const [showQuote, setShowQuote] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [pendingPaymentBooking, setPendingPaymentBooking] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Refs for autocomplete
  const pickupRef = useRef(null);
  const destinationRef = useRef(null);
  const pickupAutocomplete = useRef(null);
  const destinationAutocomplete = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Pricing constants
  const RATE_PER_KM = 10.50;
  const RATE_PER_MINUTE = 2.00;

  const [tripData, setTripData] = useState({
    // Personal Information
    passengerName: user?.name || '',
    passengerPhone: user?.phone || '',
    passengerEmail: user?.email || '',
    
    // Trip Details
    pickupAddress: '',
    destinationAddress: '',
    scheduledDate: '',
    scheduledTime: '',
    
    // Companion Requirements
    companionMinutes: 30, // Default 30 minutes
    specialRequirements: '',
    wheelchairAccess: false,
    elderlyAssistance: false,
    medicalAppointment: false,
    shoppingAssistance: false,
    
    // Additional Info
    additionalNotes: '',
    emergencyContact: '',
    emergencyPhone: '',
    
    // Terms
    acceptTerms: false,
    acceptPrivacy: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTripData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Initialize Google Places Autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.warn('Google Maps Places API not loaded');
        return;
      }

      // Initialize pickup autocomplete
      if (pickupRef.current && !pickupAutocomplete.current) {
        pickupAutocomplete.current = new window.google.maps.places.Autocomplete(pickupRef.current, {
          types: [],
          componentRestrictions: { country: 'za' }, // Restrict to South Africa
        });

        pickupAutocomplete.current.addListener('place_changed', () => {
          const place = pickupAutocomplete.current.getPlace();
          if (place && place.formatted_address) {
            setTripData(prev => ({
              ...prev,
              pickupAddress: place.formatted_address
            }));
          }
        });
      }

      // Initialize destination autocomplete
      if (destinationRef.current && !destinationAutocomplete.current) {
        destinationAutocomplete.current = new window.google.maps.places.Autocomplete(destinationRef.current, {
          types: [],
          componentRestrictions: { country: 'za' }, // Restrict to South Africa
        });

        destinationAutocomplete.current.addListener('place_changed', () => {
          const place = destinationAutocomplete.current.getPlace();
          if (place && place.formatted_address) {
            setTripData(prev => ({
              ...prev,
              destinationAddress: place.formatted_address
            }));
          }
        });
      }
    };

    // Initialize after a short delay to ensure Google Maps is loaded
    const timer = setTimeout(initAutocomplete, 1000);
    return () => clearTimeout(timer);
  }, []);

  const calculateDistanceAndTime = async () => {
    try {
      if (!window.google || !window.google.maps) {
        throw new Error('Google Maps API not loaded');
      }

      const service = new window.google.maps.DistanceMatrixService();
      
      return new Promise((resolve, reject) => {
        service.getDistanceMatrix({
          origins: [tripData.pickupAddress],
          destinations: [tripData.destinationAddress],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC,
        }, (response, status) => {
          if (status === 'OK') {
            const element = response.rows[0].elements[0];
            if (element.status === 'OK') {
              const distanceKm = element.distance.value / 1000; // Convert to km
              const durationMinutes = Math.ceil(element.duration.value / 60); // Convert to minutes
              resolve({ distance: distanceKm, duration: durationMinutes });
            } else {
              reject('Could not calculate distance and time');
            }
          } else {
            reject('Distance and time calculation failed');
          }
        });
      });
    } catch (error) {
      console.error('Distance and time calculation error:', error);
      return { distance: 0, duration: 0 };
    }
  };

  const generateQuote = async () => {
    try {
      setLoading(true);
      setError('');

      // Calculate distance and travel time (round trip = both * 2)
      const { distance: oneWayDistance, duration: oneWayDuration } = await calculateDistanceAndTime();
      
      console.log('Debug - API Response:', { oneWayDistance, oneWayDuration }); // Debug log
      
      const totalDistance = oneWayDistance * 2; // Round trip distance
      const totalTravelTime = oneWayDuration * 2; // Round trip travel time
      
      console.log('Debug - Calculations:', { 
        totalDistance, 
        totalTravelTime, 
        extraMinutes: tripData.companionMinutes 
      }); // Debug log
      
      // Total companion time = travel time + extra time user specified
      const totalCompanionTime = totalTravelTime + parseInt(tripData.companionMinutes);
      
      console.log('Debug - Final companion time:', totalCompanionTime); // Debug log
      
      // Calculate costs
      const distanceCost = totalDistance * RATE_PER_KM;
      const companionCost = totalCompanionTime * RATE_PER_MINUTE;
      const totalCost = distanceCost + companionCost;

      const quoteData = {
        oneWayDistance: oneWayDistance.toFixed(2),
        totalDistance: totalDistance.toFixed(2),
        travelTimeMinutes: totalTravelTime,
        extraCompanionMinutes: tripData.companionMinutes,
        totalCompanionMinutes: totalCompanionTime,
        distanceCost: distanceCost.toFixed(2),
        companionCost: companionCost.toFixed(2),
        totalCost: totalCost.toFixed(2),
        breakdown: {
          distanceRate: RATE_PER_KM,
          companionRate: RATE_PER_MINUTE
        }
      };

      setQuote(quoteData);
      setShowQuote(true);
    } catch (error) {
      setError('Failed to generate quote. Please check your addresses.');
    } finally {
      setLoading(false);
    }
  };

  const submitBooking = async () => {
    try {
      setLoading(true);
      setError('');

      if (!quote) {
        setError('Please generate a quote first');
        return;
      }

      if (!tripData.acceptTerms || !tripData.acceptPrivacy) {
        setError('Please accept the terms of service and privacy policy');
        return;
      }

      const bookingData = {
        ...tripData,
        userId: user.id,
        quoteDetails: quote,
        status: 'pending_assignment',
        createdAt: new Date().toISOString()
      };

      const response = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        const newBooking = await response.json();
        setPendingPaymentBooking(newBooking);
        onTripCreated(newBooking);
        
        // Show success message
        setBookingSuccess(true);
        clearDraft();
        
        // Reset form after successful booking
        setTripData({
          pickupAddress: '',
          destinationAddress: '',
          scheduledDate: '',
          scheduledTime: '',
          companionMinutes: 30,
          wheelchairAccess: false,
          elderlyAssistance: false,
          medicalAppointment: false,
          shoppingAssistance: false,
          specialRequirements: '',
          passengerName: user?.name || '',
          passengerPhone: user?.phone || '',
          passengerEmail: user?.email || '',
          additionalNotes: '',
          emergencyContact: '',
          emergencyPhone: '',
          acceptTerms: false,
          acceptPrivacy: false
        });
        
        // Reset quote and other states
        setQuote(null);
        setShowQuote(false);
        
        // Hide success message after 5 seconds
        setTimeout(() => setBookingSuccess(false), 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to submit booking');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    if (!pendingPaymentBooking) return;
    try {
      setPaymentLoading(true);
      const amount = pendingPaymentBooking.quoteDetails?.totalCost || 
                     pendingPaymentBooking.quoteDetails?.totalFare || 0;
      const callbackUrl = `${window.location.origin}${window.location.pathname}`;
      const response = await fetch(`${BACKEND_URL}/api/payments/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          booking_id: pendingPaymentBooking.id,
          amount: parseFloat(amount),
          email: user?.email || pendingPaymentBooking.passengerEmail,
          callback_url: callbackUrl
        })
      });
      const data = await response.json();
      if (response.ok && data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        alert(data.detail || 'Failed to initiate payment. Please try again from My Trips.');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Set minimum date to tomorrow (24-hour booking notice required)
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  // Save draft to localStorage whenever tripData changes
  useEffect(() => {
    const draft = { ...tripData };
    delete draft.acceptTerms;
    delete draft.acceptPrivacy;
    localStorage.setItem('booking_draft', JSON.stringify(draft));
  }, [tripData]);

  // Pre-fill form when rebooking
  useEffect(() => {
    if (reBookData) {
      setTripData(prev => ({
        ...prev,
        pickupAddress: reBookData.pickupAddress || '',
        destinationAddress: reBookData.destinationAddress || '',
        companionMinutes: reBookData.companionMinutes || 30,
        specialRequirements: reBookData.specialRequirements || '',
        wheelchairAccess: reBookData.wheelchairAccess || false,
        elderlyAssistance: reBookData.elderlyAssistance || false,
        medicalAppointment: reBookData.medicalAppointment || false,
        shoppingAssistance: reBookData.shoppingAssistance || false,
        additionalNotes: reBookData.additionalNotes || ''
      }));
    }
  }, [reBookData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore draft on mount
  useEffect(() => {
    const saved = localStorage.getItem('booking_draft');
    if (saved) {
      try {
        const draft = JSON.parse(saved);
        setTripData(prev => ({ ...prev, ...draft }));
      } catch { /* ignore */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearDraft = () => localStorage.removeItem('booking_draft');

  const handleStartFromHome = () => {
    if (user?.home_address) {
      setTripData(prev => ({ ...prev, pickupAddress: user.home_address }));
      if (pickupRef.current) pickupRef.current.value = user.home_address;
    }
  };

  return (
    <div className="trip-booking-form">
      <div className="form-header">
        <h2>Book Your Companion Ride</h2>
        <p>Fill in all details for your personalized companion transportation service</p>
      </div>

      <form className="booking-form">
        {error && <div className="error-message">{error}</div>}
        {bookingSuccess && (
          <div className="success-message" style={{ textAlign: 'center', padding: '1.5rem', borderRadius: '12px', background: '#f0fdf4', border: '1px solid #22c55e' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎉</div>
            <strong style={{ display: 'block', marginBottom: '0.25rem', color: '#166534', fontSize: '1.1rem' }}>Booking Confirmed!</strong>
            <p style={{ color: '#166534', fontSize: '0.9rem', margin: '0 0 1rem' }}>
              We'll assign a driver and be in touch soon.
            </p>
            {pendingPaymentBooking && (
              <div style={{ background: 'white', borderRadius: '10px', padding: '1rem', border: '1px solid #A0306A', marginBottom: '0.75rem' }}>
                <div style={{ color: '#A0306A', fontWeight: '700', fontSize: '1.2rem', marginBottom: '0.25rem' }}>
                  R{pendingPaymentBooking.quoteDetails?.totalCost || pendingPaymentBooking.quoteDetails?.totalFare}
                </div>
                <div style={{ color: '#666', fontSize: '0.85rem', marginBottom: '0.75rem' }}>Due now to secure your trip</div>
                <button
                  type="button"
                  data-testid="pay-now-btn"
                  onClick={handlePayNow}
                  disabled={paymentLoading}
                  style={{
                    background: '#A0306A', color: 'white', border: 'none',
                    borderRadius: '8px', padding: '0.7rem 2rem',
                    fontSize: '1rem', fontWeight: '600', cursor: 'pointer',
                    width: '100%', opacity: paymentLoading ? 0.7 : 1
                  }}
                >
                  {paymentLoading ? 'Redirecting...' : 'Pay Now with Paystack'}
                </button>
                <p style={{ color: '#888', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                  You can also pay later from My Trips
                </p>
              </div>
            )}
          </div>
        )}

        {/* Personal Information */}
        <section className="form-section">
          <h3>Personal Information</h3>
          <div className="form-row">
            <div className="input-group">
              <label htmlFor="passenger-name">Full Name *</label>
              <input
                type="text"
                id="passenger-name"
                name="passengerName"
                value={tripData.passengerName}
                onChange={handleInputChange}
                autoComplete="name"
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="passenger-phone">Phone Number *</label>
              <input
                type="tel"
                id="passenger-phone"
                name="passengerPhone"
                value={tripData.passengerPhone}
                onChange={handleInputChange}
                required
                className="form-input"
                autoComplete="tel"
              />
            </div>
          </div>
          <div className="input-group">
            <label htmlFor="passenger-email">Email Address *</label>
            <input
              type="email"
              id="passenger-email"
              name="passengerEmail"
              value={tripData.passengerEmail}
              onChange={handleInputChange}
              required
              className="form-input"
              autoComplete="email"
            />
          </div>
        </section>

        {/* Trip Details */}
        <section className="form-section">
          <h3>Trip Details</h3>
          <div className="input-group">
            <label htmlFor="pickup-address">Pickup Address (Round Trip Start/End) *</label>
            {user?.home_address && (
              <button
                type="button"
                className="start-from-home-btn"
                onClick={handleStartFromHome}
                data-testid="start-from-home-btn"
                style={{
                  marginBottom: '0.5rem',
                  padding: '0.35rem 0.75rem',
                  background: '#F7D6E8',
                  border: '1px solid #A0306A',
                  borderRadius: '20px',
                  color: '#A0306A',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}
              >
                <span>🏠</span> Start from Home
              </button>
            )}
            <input
              ref={pickupRef}
              type="text"
              id="pickup-address"
              name="pickupAddress"
              value={tripData.pickupAddress}
              onChange={handleInputChange}
              placeholder="Start typing your pickup location..."
              autoComplete="street-address"
              required
            />
          </div>
          <div className="input-group">
            <label htmlFor="destination-address">Destination Address (Round Trip Middle) *</label>
            <input
              ref={destinationRef}
              type="text"
              id="destination-address"
              name="destinationAddress"
              value={tripData.destinationAddress}
              onChange={handleInputChange}
              placeholder="Start typing your destination..."
              autoComplete="street-address"
              required
            />
            <small>We calculate round trip: Pickup → Destination → Pickup</small>
          </div>
          <div className="form-row">
            <div className="input-group">
              <label htmlFor="scheduled-date">Scheduled Date *</label>
              <input
                type="date"
                id="scheduled-date"
                name="scheduledDate"
                value={tripData.scheduledDate}
                onChange={handleInputChange}
                min={tomorrow}
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="scheduled-time">Scheduled Time *</label>
              <input
                type="time"
                id="scheduled-time"
                name="scheduledTime"
                value={tripData.scheduledTime}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </section>

        {/* Companion Requirements */}
        <section className="form-section">
          <h3>Companion Service Requirements</h3>
          <div className="input-group">
            <label htmlFor="companion-minutes">Extra Companion Time Needed (minutes) *</label>
            <input
              type="number"
              id="companion-minutes"
              name="companionMinutes"
              value={tripData.companionMinutes}
              onChange={handleInputChange}
              min="15"
              max="480"
              step="15"
              required
            />
            <small>Add extra time needed beyond travel time (min 15 min, max 8 hours)</small>
          </div>

          <div className="checkbox-group">
            <h4>Special Requirements</h4>
            <label className="checkbox-label">
              <input
                type="checkbox"
                id="elderly-assistance"
                name="elderlyAssistance"
                checked={tripData.elderlyAssistance}
                onChange={handleInputChange}
              />
              <span>Elderly assistance needed</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                id="medical-appointment"
                name="medicalAppointment"
                checked={tripData.medicalAppointment}
                onChange={handleInputChange}
              />
              <span>Medical appointment transport</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                id="shopping-assistance"
                name="shoppingAssistance"
                checked={tripData.shoppingAssistance}
                onChange={handleInputChange}
              />
              <span>Shopping/errands assistance</span>
            </label>
          </div>

          <div className="input-group">
            <label htmlFor="special-requirements">Additional Requirements</label>
            <textarea
              id="special-requirements"
              name="specialRequirements"
              value={tripData.specialRequirements}
              onChange={handleInputChange}
              placeholder="Describe any specific needs or requirements"
              rows="3"
            />
          </div>
        </section>

        {/* Emergency Contact */}
        <section className="form-section">
          <h3>Emergency Contact</h3>
          <div className="form-row">
            <div className="input-group">
              <label htmlFor="emergency-contact">Emergency Contact Name</label>
              <input
                type="text"
                id="emergency-contact"
                name="emergencyContact"
                value={tripData.emergencyContact}
                onChange={handleInputChange}
                autoComplete="name"
              />
            </div>
            <div className="input-group">
              <label htmlFor="emergency-phone">Emergency Contact Phone</label>
              <input
                type="tel"
                id="emergency-phone"
                name="emergencyPhone"
                value={tripData.emergencyPhone}
                onChange={handleInputChange}
                autoComplete="tel"
              />
            </div>
          </div>
        </section>

        {/* Additional Notes */}
        <section className="form-section">
          <div className="input-group">
            <label htmlFor="additional-notes">Additional Notes</label>
            <textarea
              id="additional-notes"
              name="additionalNotes"
              value={tripData.additionalNotes}
              onChange={handleInputChange}
              placeholder="Any other information that would help us serve you better"
              rows="3"
            />
          </div>
        </section>

        {/* Generate Quote */}
        <div className="quote-section">
          {(!tripData.pickupAddress || !tripData.destinationAddress) && (
            <p style={{ color: '#A0306A', fontSize: '0.85rem', marginBottom: '0.5rem', padding: '0.5rem 0.75rem', background: '#FFF0F8', borderRadius: '8px', border: '1px solid #F7D6E8' }} data-testid="address-required-notice">
              Please enter both a pickup address and destination to generate your quote.
            </p>
          )}
          <button
            type="button"
            onClick={generateQuote}
            disabled={loading || !tripData.pickupAddress || !tripData.destinationAddress}
            className="btn-secondary"
            data-testid="generate-quote-btn"
          >
            {loading ? 'Calculating...' : 'Generate Quote'}
          </button>
        </div>

        {/* Quote Display */}
        {showQuote && quote && (
          <section className="quote-display">
            <h3>Your Trip Quote</h3>
            <div className="quote-details">
              <div className="quote-row">
                <span>Distance (round trip):</span>
                <span>{quote.totalDistance} km</span>
              </div>
              <div className="quote-row">
                <span>Companion time:</span>
                <span>{quote.totalCompanionMinutes} minutes</span>
              </div>
              <div className="quote-row">
                <span>Distance cost (R{quote.breakdown.distanceRate}/km):</span>
                <span>R {quote.distanceCost}</span>
              </div>
              <div className="quote-row">
                <span>Companion cost (R{quote.breakdown.companionRate}/min):</span>
                <span>R {quote.companionCost}</span>
              </div>
              <div className="quote-total">
                <span>Total Estimated Cost:</span>
                <span>R {quote.totalCost}</span>
              </div>
            </div>
          </section>
        )}

        {/* Terms and Conditions */}
        {showQuote && (
          <section className="terms-section">
            <h3>Terms and Conditions</h3>
            <label className="checkbox-label">
              <input
                type="checkbox"
                id="accept-terms"
                name="acceptTerms"
                checked={tripData.acceptTerms}
                onChange={handleInputChange}
                required
              />
              <span>I accept the Terms of Service *</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                id="accept-privacy"
                name="acceptPrivacy"
                checked={tripData.acceptPrivacy}
                onChange={handleInputChange}
                required
              />
              <span>I accept the Privacy Policy *</span>
            </label>
          </section>
        )}

        {/* Submit Button */}
        {showQuote && (
          <div className="submit-section">
            <button
              type="button"
              onClick={submitBooking}
              disabled={loading || !tripData.acceptTerms || !tripData.acceptPrivacy}
              className="btn-primary large"
            >
              {loading ? 'Submitting...' : 'Book My Ride'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default TripBookingForm;